import { useRouter } from 'next/router';
import { ReactNode, useEffect, useMemo } from 'react';
import { useMercoaSession } from '@mercoa/react';
import LoadingScreen from './LoadingScreen';
import { AdminRoutes } from 'routes';
import { Mercoa } from '@mercoa/javascript';
import { useCompany } from 'context/company';
import { PROD_DEMO_ACC_COMPANYID } from 'lib/whitelistConstants';

interface APAROnboardingWrapperProps {
  children: ReactNode;
}

// KYB statuses that should show informational message but still allow access
const KYB_INFORMATIONAL_STATUSES = [
  Mercoa.EntityStatus.Pending,
  Mercoa.EntityStatus.Review,
  Mercoa.EntityStatus.Resubmit,
] as const;

// Helper function to get KYB status display text
const getKYBStatusText = (status: string): string => {
  switch (status) {
    case Mercoa.EntityStatus.Pending:
      return 'is pending';
    case Mercoa.EntityStatus.Review:
      return 'is under review';
    case Mercoa.EntityStatus.Resubmit:
      return 'requires resubmission';
    default:
      return 'is being processed';
  }
};

/**
 * KYB Status Banner Component - Shows message about KYB status and blocks access until verified
 */
const KYBStatusBanner = ({ status }: { status: string }) => (
  <div className="mt-48 flex w-full items-center justify-center">
    <div className="flex max-w-md flex-col items-center justify-center text-center">
      <div className="p-6">
        <p className="mb-2 text-2xl font-medium text-gray-900">Your KYB {getKYBStatusText(status)}</p>
        <p className="text-sm text-gray-600">
          Please wait for your KYB to be verified. We will notify you once your KYB is verified.
        </p>
      </div>
    </div>
  </div>
);

/**
 * This wrapper is used to redirect users to the AP/AR onboarding page if they are not fully onboarded.
 * It is used in the AP/AR pages to gate access based on entity status and app enablement.
 */
export const APAROnboardingWrapper = ({ children }: APAROnboardingWrapperProps) => {
  const router = useRouter();
  const mercoaSession = useMercoaSession();
  const { company, isLoading: isCompanyLoading } = useCompany() || {};
  const is_THERA_DEMO_COMPANY = company?.companyID === PROD_DEMO_ACC_COMPANYID;

  // Memoize derived values to prevent unnecessary re-renders
  const { isAPEnabled, isAREnabled, entityStatus, hasKYBInformationalStatus, isVerified } = useMemo(() => {
    const settings = company?.settings;
    const entityStatus = mercoaSession?.entity?.status;

    return {
      isAPEnabled: settings?.enabledApps?.ap,
      isAREnabled: settings?.enabledApps?.ar,
      entityStatus,
      hasKYBInformationalStatus: entityStatus ? KYB_INFORMATIONAL_STATUSES.includes(entityStatus as any) : false,
      isVerified: entityStatus === Mercoa.EntityStatus.Verified,
    };
  }, [company?.settings, mercoaSession?.entity?.status]);

  // Redirect to appropriate onboarding if entity exists but cannot access AP/AR
  useEffect(() => {
    const entity = mercoaSession?.entity;
    if (entity && !canEntityAccessAPAR(entity)) {
      const targetRoute = isAPEnabled ? AdminRoutes.APOnboarding : isAREnabled ? AdminRoutes.AROnboarding : null;

      if (targetRoute) {
        router.replace(targetRoute);
      }
    }
  }, [isAPEnabled, isAREnabled, mercoaSession?.entity, router]);

  // Show loading screen while data is being fetched
  if (!mercoaSession?.entity || isCompanyLoading) {
    return <LoadingScreen />;
  }

  // Handle different KYB statuses explicitly
  if (hasKYBInformationalStatus && entityStatus && !is_THERA_DEMO_COMPANY) {
    // Show only banner for review, pending, resubmit statuses (no children as actionable items would fail)
    return <KYBStatusBanner status={entityStatus} />;
  }

  if (isVerified || is_THERA_DEMO_COMPANY) {
    // Show children directly for verified status or Thera demo company
    return children;
  }

  // For all other statuses (unverified, etc.), redirect to onboarding or show loading
  if (mercoaSession.entity && !canEntityAccessAPAR(mercoaSession.entity)) {
    // Loading screen while redirecting to onboarding
    return <LoadingScreen />;
  }

  // Default loading screen
  return <LoadingScreen />;
};

/**
 * Determines if an entity can access AP/AR functionality based on their status.
 * We allow entities with pending, verified, review, and resubmit statuses to access AP/AR.
 * Unverified entities must complete onboarding first.
 */
export const canEntityAccessAPAR = (entity: Mercoa.EntityResponse): boolean => {
  const allowedStatuses = [
    Mercoa.EntityStatus.Pending,
    Mercoa.EntityStatus.Verified,
    Mercoa.EntityStatus.Review,
    Mercoa.EntityStatus.Resubmit,
  ];

  return allowedStatuses.includes(entity.status as any);
};
